import { useState } from 'react'
import {
  Button,
  Table,
  Tag,
  Popconfirm,
  Select,
  InputNumber,
  Input,
  Form,
  Tabs,
  Space,
  Typography,
  message,
} from 'antd'
import { EditOutlined, DeleteOutlined, PlusOutlined } from '@ant-design/icons'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import dayjs from 'dayjs'
import apiClient from '@/shared/api/client'
import PageHeader from '@/shared/components/PageHeader'
import FormDrawer from '@/shared/components/FormDrawer'
import { usePlantId } from '@/shared/hooks/usePlantId'

const { Text } = Typography

const MONTHS = [
  { value: 1, label: 'January' },
  { value: 2, label: 'February' },
  { value: 3, label: 'March' },
  { value: 4, label: 'April' },
  { value: 5, label: 'May' },
  { value: 6, label: 'June' },
  { value: 7, label: 'July' },
  { value: 8, label: 'August' },
  { value: 9, label: 'September' },
  { value: 10, label: 'October' },
  { value: 11, label: 'November' },
  { value: 12, label: 'December' },
]

const BUDGET_TYPES = ['OPEX', 'CAPEX', 'REVENUE']

const TYPE_COLOR: Record<string, string> = {
  OPEX: 'orange',
  CAPEX: 'blue',
  REVENUE: 'green',
}

function fmt(val: number | null | undefined) {
  if (val == null) return '—'
  return `₹ ${Number(val).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`
}

interface BudgetEntry {
  id: number
  plant_id: number
  ledger_id?: number
  department_id?: number
  budget_year: number
  budget_month?: number
  budget_type: string
  description: string
  budgeted_amount: number
  actual_amount?: number
  variance?: number
  ledger_name?: string
  department_name?: string
}

interface VarianceRow {
  id?: number
  description: string
  budget_type: string
  budgeted_amount: number
  actual_amount: number
  variance: number
  variance_pct?: number
}

export default function BudgetPage() {
  const { plantId, plants, setPlantId } = usePlantId()
  const [year, setYear] = useState<number>(dayjs().year())
  const [month, setMonth] = useState<number | null>(null)
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [editingRecord, setEditingRecord] = useState<BudgetEntry | null>(null)
  const [form] = Form.useForm()
  const queryClient = useQueryClient()

  // Fetch budgets
  const budgetsQuery = useQuery<BudgetEntry[]>({
    queryKey: ['mis-budgets', plantId, year],
    queryFn: async () => {
      const res = await apiClient.get('/mis/budgets', {
        params: { plant_id: plantId, budget_year: year },
      })
      return res.data?.items ?? res.data ?? []
    },
    enabled: !!plantId,
  })

  // Fetch variance
  const varianceQuery = useQuery<VarianceRow[]>({
    queryKey: ['mis-budget-variance', plantId, year, month],
    queryFn: async () => {
      const params: Record<string, unknown> = { plant_id: plantId, budget_year: year }
      if (month) params.budget_month = month
      const res = await apiClient.get('/mis/budget-variance', { params })
      return res.data?.items ?? res.data ?? []
    },
    enabled: !!plantId,
  })

  // Fetch ledgers
  const ledgersQuery = useQuery<{ id: number; ledger_name: string; ledger_code: string }[]>({
    queryKey: ['accounting-ledgers', plantId],
    queryFn: async () => {
      const res = await apiClient.get('/accounting/ledgers', {
        params: { plant_id: plantId, limit: 500 },
      })
      return res.data?.items ?? res.data ?? []
    },
    enabled: !!plantId,
  })

  // Fetch departments
  const deptsQuery = useQuery<{ id: string; name: string }[]>({
    queryKey: ['departments'],
    queryFn: async () => {
      const res = await apiClient.get('/departments', { params: { page_size: 200 } })
      return res.data?.items ?? res.data ?? []
    },
  })

  const createMutation = useMutation({
    mutationFn: (data: unknown) => apiClient.post('/mis/budgets', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['mis-budgets'] })
      queryClient.invalidateQueries({ queryKey: ['mis-budget-variance'] })
      message.success('Budget entry created')
      setDrawerOpen(false)
      form.resetFields()
    },
    onError: () => message.error('Failed to create budget entry'),
  })

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: unknown }) =>
      apiClient.patch(`/mis/budgets/${id}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['mis-budgets'] })
      queryClient.invalidateQueries({ queryKey: ['mis-budget-variance'] })
      message.success('Budget entry updated')
      setDrawerOpen(false)
      form.resetFields()
      setEditingRecord(null)
    },
    onError: () => message.error('Failed to update budget entry'),
  })

  const deleteMutation = useMutation({
    mutationFn: (id: number) => apiClient.delete(`/mis/budgets/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['mis-budgets'] })
      queryClient.invalidateQueries({ queryKey: ['mis-budget-variance'] })
      message.success('Budget entry deleted')
    },
    onError: () => message.error('Failed to delete budget entry'),
  })

  const openAdd = () => {
    setEditingRecord(null)
    form.resetFields()
    form.setFieldsValue({ plant_id: plantId, budget_year: year })
    setDrawerOpen(true)
  }

  const openEdit = (record: BudgetEntry) => {
    setEditingRecord(record)
    form.setFieldsValue({ ...record })
    setDrawerOpen(true)
  }

  const handleSubmit = () => {
    form.validateFields().then((values) => {
      if (editingRecord) {
        updateMutation.mutate({ id: editingRecord.id, data: values })
      } else {
        createMutation.mutate({ ...values, plant_id: plantId })
      }
    })
  }

  const budgetColumns = [
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
    },
    {
      title: 'Type',
      dataIndex: 'budget_type',
      key: 'budget_type',
      render: (t: string) => <Tag color={TYPE_COLOR[t] ?? 'default'}>{t}</Tag>,
    },
    {
      title: 'Year',
      dataIndex: 'budget_year',
      key: 'budget_year',
      width: 80,
    },
    {
      title: 'Month',
      dataIndex: 'budget_month',
      key: 'budget_month',
      width: 100,
      render: (m: number) => (m ? MONTHS.find((x) => x.value === m)?.label ?? m : '—'),
    },
    {
      title: 'Ledger',
      dataIndex: 'ledger_name',
      key: 'ledger_name',
      render: (v: string) => v ?? '—',
    },
    {
      title: 'Department',
      dataIndex: 'department_name',
      key: 'department_name',
      render: (v: string) => v ?? '—',
    },
    {
      title: 'Budgeted (₹)',
      dataIndex: 'budgeted_amount',
      key: 'budgeted_amount',
      align: 'right' as const,
      render: (v: number) => fmt(v),
    },
    {
      title: 'Actual (₹)',
      dataIndex: 'actual_amount',
      key: 'actual_amount',
      align: 'right' as const,
      render: (v: number) => fmt(v),
    },
    {
      title: 'Variance (₹)',
      dataIndex: 'variance',
      key: 'variance',
      align: 'right' as const,
      render: (v: number) =>
        v == null ? '—' : (
          <span style={{ color: v < 0 ? '#ff4d4f' : undefined }}>{fmt(v)}</span>
        ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 100,
      render: (_: unknown, record: BudgetEntry) => (
        <Space>
          <Button
            icon={<EditOutlined />}
            size="small"
            type="text"
            onClick={() => openEdit(record)}
          />
          <Popconfirm
            title="Delete this budget entry?"
            onConfirm={() => deleteMutation.mutate(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Button icon={<DeleteOutlined />} size="small" type="text" danger />
          </Popconfirm>
        </Space>
      ),
    },
  ]

  const varianceColumns = [
    { title: 'Description', dataIndex: 'description', key: 'description' },
    {
      title: 'Type',
      dataIndex: 'budget_type',
      key: 'budget_type',
      render: (t: string) => <Tag color={TYPE_COLOR[t] ?? 'default'}>{t}</Tag>,
    },
    {
      title: 'Budgeted (₹)',
      dataIndex: 'budgeted_amount',
      key: 'budgeted_amount',
      align: 'right' as const,
      render: (v: number) => fmt(v),
    },
    {
      title: 'Actual (₹)',
      dataIndex: 'actual_amount',
      key: 'actual_amount',
      align: 'right' as const,
      render: (v: number) => fmt(v),
    },
    {
      title: 'Variance (₹)',
      dataIndex: 'variance',
      key: 'variance',
      align: 'right' as const,
      render: (v: number) =>
        v == null ? '—' : (
          <Text style={{ color: v < 0 ? '#ff4d4f' : '#52c41a' }}>{fmt(v)}</Text>
        ),
    },
    {
      title: 'Variance %',
      dataIndex: 'variance_pct',
      key: 'variance_pct',
      align: 'right' as const,
      render: (v: number, record: VarianceRow) => {
        const pct =
          v != null
            ? v
            : record.budgeted_amount
              ? ((record.variance / record.budgeted_amount) * 100)
              : null
        if (pct == null) return '—'
        return (
          <Text style={{ color: pct < 0 ? '#ff4d4f' : '#52c41a' }}>
            {pct.toFixed(1)}%
          </Text>
        )
      },
    },
  ]

  const isSubmitting = createMutation.isPending || updateMutation.isPending

  return (
    <>
      <PageHeader
        title="Budget & Variance"
        subtitle="Manage budgets and track actuals vs budgeted amounts"
        extra={
          <Space wrap>
            <Select
              value={plantId}
              onChange={setPlantId}
              style={{ width: 180 }}
              placeholder="Select plant"
              options={plants?.map((p: { id: string; name: string }) => ({
                value: p.id,
                label: p.name,
              }))}
            />
            <InputNumber
              value={year}
              onChange={(v) => setYear(v ?? dayjs().year())}
              min={2000}
              max={2100}
              style={{ width: 100 }}
              placeholder="Year"
            />
            <Select
              value={month}
              onChange={(v) => setMonth(v)}
              style={{ width: 140 }}
              placeholder="All months"
              allowClear
              options={MONTHS}
            />
          </Space>
        }
      />

      <Tabs
        defaultActiveKey="entries"
        style={{ padding: '0 24px' }}
        items={[
          {
            key: 'entries',
            label: 'Budget Entries',
            children: (
              <>
                <div style={{ marginBottom: 16 }}>
                  <Button type="primary" icon={<PlusOutlined />} onClick={openAdd}>
                    Add Budget
                  </Button>
                </div>
                <Table
                  dataSource={budgetsQuery.data ?? []}
                  columns={budgetColumns}
                  rowKey="id"
                  loading={budgetsQuery.isLoading}
                  size="small"
                  scroll={{ x: 1000 }}
                  pagination={{ pageSize: 20 }}
                />
              </>
            ),
          },
          {
            key: 'variance',
            label: 'Variance Report',
            children: (
              <Table
                dataSource={varianceQuery.data ?? []}
                columns={varianceColumns}
                rowKey={(r, i) => r.id ?? i ?? 0}
                loading={varianceQuery.isLoading}
                size="small"
                scroll={{ x: 800 }}
                pagination={{ pageSize: 20 }}
              />
            ),
          },
        ]}
      />

      <FormDrawer
        title={editingRecord ? 'Edit Budget Entry' : 'Add Budget Entry'}
        open={drawerOpen}
        onClose={() => {
          setDrawerOpen(false)
          setEditingRecord(null)
          form.resetFields()
        }}
        onSubmit={handleSubmit}
        loading={isSubmitting}
        form={form}
        width={480}
      >
        <Form form={form} layout="vertical">
          <Form.Item name="plant_id" label="Plant" rules={[{ required: true }]}>
            <Select
              options={plants?.map((p: { id: string; name: string }) => ({
                value: p.id,
                label: p.name,
              }))}
              placeholder="Select plant"
            />
          </Form.Item>

          <Form.Item
            name="budget_type"
            label="Budget Type"
            rules={[{ required: true, message: 'Budget type is required' }]}
          >
            <Select
              options={BUDGET_TYPES.map((t) => ({ value: t, label: t }))}
              placeholder="Select type"
            />
          </Form.Item>

          <Form.Item
            name="budget_year"
            label="Budget Year"
            rules={[{ required: true, message: 'Year is required' }]}
          >
            <InputNumber min={2000} max={2100} style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item name="budget_month" label="Budget Month (optional)">
            <Select options={MONTHS} placeholder="Select month" allowClear />
          </Form.Item>

          <Form.Item name="ledger_id" label="Ledger (optional)">
            <Select
              showSearch
              optionFilterProp="label"
              placeholder="Search ledger"
              allowClear
              loading={ledgersQuery.isLoading}
              options={(ledgersQuery.data ?? []).map((l) => ({
                value: l.id,
                label: `${l.ledger_code} — ${l.ledger_name}`,
              }))}
            />
          </Form.Item>

          <Form.Item name="department_id" label="Department (optional)">
            <Select
              showSearch
              optionFilterProp="label"
              placeholder="Select department"
              allowClear
              loading={deptsQuery.isLoading}
              options={(deptsQuery.data ?? []).map((d) => ({
                value: d.id,
                label: d.name,
              }))}
            />
          </Form.Item>

          <Form.Item
            name="description"
            label="Description"
            rules={[{ required: true, message: 'Description is required' }]}
          >
            <Input placeholder="Enter description" />
          </Form.Item>

          <Form.Item
            name="budgeted_amount"
            label="Budgeted Amount (₹)"
            rules={[{ required: true, message: 'Amount is required' }]}
          >
            <InputNumber
              style={{ width: '100%' }}
              min={0}
              precision={2}
              placeholder="0.00"
              formatter={(v) => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
              parser={(v) => (v ? v.replace(/,/g, '') : '0') as any}
            />
          </Form.Item>
        </Form>
      </FormDrawer>
    </>
  )
}
